"use client";

import { useRef, useCallback, useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useProfilePreviewStore } from "../model/profile-preview-store";
import { PREVIEW_HOVER_DELAY_MS } from "../hooks/use-profile-preview";
import { userApi } from "@/shared/api/user";

function getProfileQueryKey(userId: string | null, username: string | null) {
  if (userId) return ["profile-preview", "id", userId];
  if (username) return ["profile-preview", "username", username];
  return ["profile-preview", "none"];
}

interface ProfilePreviewTriggerProps {
  userId?: string | null;
  username?: string | null;
  children: React.ReactNode;
  className?: string;
  asChild?: boolean;
}

export function ProfilePreviewTrigger({
  userId,
  username,
  children,
  className,
}: ProfilePreviewTriggerProps) {
  const triggerRef = useRef<HTMLDivElement>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleCbRef = useRef<number | null>(null);
  const queryClient = useQueryClient();

  const open = useProfilePreviewStore((s) => s.open);
  const close = useProfilePreviewStore((s) => s.close);
  const state = useProfilePreviewStore((s) => s.state);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    // KAN-298: отменяем и отложенный prefetch — быстрые пролёты мыши по
    // нескольким аватарам не должны спамить запросами
    if (idleCbRef.current !== null) {
      if (typeof cancelIdleCallback === "function") {
        cancelIdleCallback(idleCbRef.current);
      } else {
        clearTimeout(idleCbRef.current);
      }
      idleCbRef.current = null;
    }
  }, []);

  const prefetchProfile = useCallback(() => {
    if (!userId && !username) return;
    queryClient.prefetchQuery({
      queryKey: getProfileQueryKey(userId ?? null, username ?? null),
      queryFn: async () => {
        if (userId) return userApi.getUserById(userId);
        if (username) return userApi.getUserByUsername(username);
        return null;
      },
      staleTime: 90_000,
    });
  }, [userId, username, queryClient]);

  const handleMouseEnter = useCallback(() => {
    clearHoverTimer();
    // KAN-298: prefetch не в синхронном pointer-хендлере (INP), а в idle-слоте
    idleCbRef.current =
      typeof requestIdleCallback === "function"
        ? requestIdleCallback(
            () => {
              idleCbRef.current = null;
              prefetchProfile();
            },
            { timeout: 200 },
          )
        : (setTimeout(() => {
            idleCbRef.current = null;
            prefetchProfile();
          }, 50) as unknown as number);
    hoverTimerRef.current = setTimeout(() => {
      hoverTimerRef.current = null;
      const el = triggerRef.current;
      if (!el || (!userId && !username)) return;
      const rect = el.getBoundingClientRect();
      open({
        userId: userId ?? null,
        username: username ?? null,
        anchor: {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        },
      });
    }, PREVIEW_HOVER_DELAY_MS);
  }, [userId, username, open, clearHoverTimer, prefetchProfile]);

  const handleClose = useCallback(() => {
    clearHoverTimer();
    if (state === "hover") close();
  }, [state, close, clearHoverTimer]);

  // Клик по нику = переход в профиль: гасим отложенный hover-таймер и закрываем
  // карточку, иначе она всплывёт уже на странице профиля.
  const handleClick = useCallback(() => {
    clearHoverTimer();
    close();
  }, [clearHoverTimer, close]);

  useEffect(() => {
    return clearHoverTimer;
  }, [clearHoverTimer]);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- обёртка над ссылкой на профиль: клик (в т.ч. Enter по ссылке) всплывает сюда лишь чтобы закрыть карточку
    <div
      ref={triggerRef}
      className={className}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleClose}
      onClick={handleClick}
    >
      {children}
    </div>
  );
}
